import type {
  PaymentApprovalPolicy,
  PaymentStage,
  SignoffStageMember,
  SignoffStageMode,
  Signatory,
  StoredPaymentApproval,
} from './types';
import type { InvoiceLine, PaymentChainRow } from './projects';
import { whoMustSign, isSomebody, openStep, howFar, type TheWorld } from './signoff-policy';
import { effectiveRole, ROLE_LABEL } from './permissions';
import { displayName } from './people';

/**
 * ── WHO AUTHORISES A PAYMENT — the configurable chain (executive-approver tier) ──
 *
 * Payment authorisation is an ORDERED CHAIN, the same primitive as the sign-off
 * chain and resolved by the same machinery (this module calls straight into
 * signoff-policy for who-a-role-is, the order gate and the settled-state). It adds
 * only what money needs that a deliverable sign-off does not: a VALUE BAND that
 * decides which stages a given payment must pass, and an AUTHORITY RULE that —
 * unlike the old single-approver path — never lets a workspace admin approve just
 * for being an admin. An admin CONFIGURES the chain; the chain says who may sign.
 *
 * Pure: everything comes in as arguments, so it tests without a database. Vinnie
 * approved the model and its three surfaces on 4 Oct 2026.
 */

const tidy = (s: string | undefined): string => (s ?? '').trim();
const lc = (s: string | undefined): string => tidy(s).toLowerCase();

/* ── THE STORED SHAPE, READ FORWARD ───────────────────────────────────────────
   Mirrors signoff-policy's readConfig/policyForType: a board carries a workspace
   DEFAULT chain plus optional per-project overrides. A project reads its override
   if it set one, else the default. NOTHING here rewrites what is stored. */

export function readPaymentConfig(
  stored: StoredPaymentApproval | undefined | null
): { default: PaymentApprovalPolicy | null; overrides: Record<string, PaymentApprovalPolicy> } {
  const def = stored?.default && Array.isArray(stored.default.stages) ? stored.default : null;
  const overrides: Record<string, PaymentApprovalPolicy> = {};
  const src = stored?.overrides;
  if (src && typeof src === 'object')
    for (const [key, value] of Object.entries(src))
      if (value && typeof value === 'object' && Array.isArray(value.stages))
        overrides[key] = value as PaymentApprovalPolicy;
  return { default: def, overrides };
}

/**
 * THE CHAIN THAT GOVERNS ONE PROJECT'S PAYMENTS — its override if set, else the
 * workspace default. `null` means NO chain is set: approval is BLOCKED (never a
 * silent "any admin"), surfaced as the "Set a payment approver" first-run state.
 */
export function policyForProject(
  stored: StoredPaymentApproval | undefined | null,
  projectId: string
): PaymentApprovalPolicy | null {
  const { default: def, overrides } = readPaymentConfig(stored);
  return overrides[projectId] ?? def ?? null;
}

/* ── BANDS: WHICH STAGES A PAYMENT OF THIS SIZE MUST PASS ──────────────────────
   A stage with no band applies to every payment; a banded stage applies only when
   the line's base-currency amount is at or above its threshold. Crucially a band
   ADDS a stage above the threshold — the stages below it still apply — so the
   executive signs IN ADDITION to the manager, never instead. */

/** The stages this payment must pass, in order, given the amount already converted
 *  to the workspace base currency. */
export function applicableStages(policy: PaymentApprovalPolicy, baseAmount: number): PaymentStage[] {
  return [...policy.stages]
    .filter((s) => !!s && typeof s === 'object' && !!tidy(s.id))
    .filter((s) => !s.band || baseAmount >= s.band.minAmountBase)
    .sort((a, b) => a.order - b.order);
}

/** How many of a stage's resolved members must sign, clamped to something
 *  answerable (mirrors signoff-policy's readStageMode against the real people). */
function clampMode(mode: SignoffStageMode | undefined, memberCount: number): SignoffStageMode {
  if (mode === 'anyOne') return 'anyOne';
  if (mode && typeof mode === 'object' && typeof mode.quorum === 'number' && Number.isFinite(mode.quorum)) {
    const q = Math.floor(mode.quorum);
    if (q <= 1) return 'anyOne';
    if (memberCount > 0 && q >= memberCount) return 'all';
    return { quorum: q };
  }
  return 'all';
}

/** One payment stage's members turned into people, reusing the sign-off resolver
 *  for role members and resolving named/external members the same way it does. One
 *  row per person within a stage (first place kept, last description kept). */
function resolveStageMembers(members: SignoffStageMember[], world: TheWorld): Signatory[] {
  const out: Signatory[] = [];
  for (const m of members) {
    if (m.kind === 'role' && m.role) {
      out.push(...whoMustSign({ roles: [m.role], named: [] }, world));
    } else if (m.kind === 'person' && m.personRef) {
      const email = lc(m.personRef);
      const role = effectiveRole({
        email,
        admins: world.access.admins,
        defaults: world.roles as Record<string, string>,
        on: world.projectRoles as Record<string, Record<string, string>>,
        projectId: world.projectId,
      });
      out.push({ name: displayName(email, world.profiles), position: ROLE_LABEL[role], email });
    } else if (m.kind === 'external') {
      out.push({ name: tidy(m.name), position: tidy(m.position), email: tidy(m.email) });
    }
  }
  const sameAs = (s: Signatory): string =>
    lc(s.email) || `${lc(s.name)}|${lc(s.position)}`;
  const seen = new Map<string, number>();
  const rows: Signatory[] = [];
  for (const who of out) {
    if (!isSomebody(who)) continue;
    const key = sameAs(who);
    const at = seen.get(key);
    if (at === undefined) {
      seen.set(key, rows.length);
      rows.push(who);
    } else {
      rows[at] = { ...rows[at], position: who.position, email: rows[at].email || who.email };
    }
  }
  return rows;
}

/**
 * THE WHOLE CHAIN A PAYMENT MUST PASS, RESOLVED TO PEOPLE AND IN ORDER — the rows
 * the store FREEZES onto the line (`InvoiceLine.payChain`) the moment authorisation
 * first opens. Stages are the ones the amount makes applicable, sorted by `order`
 * then DENSE-RANKED (0,1,2… no gaps) into `stageSeq` — the order gate the chain
 * reads, exactly as the sign-off chain freezes `stage_seq`. Within a stage, members
 * in resolve order. A non-'all' stage carries its mode and the band (if any) onto
 * every row, for the settle logic and the drawer's "above £X" chip.
 */
export function resolvePaymentChain(
  policy: PaymentApprovalPolicy,
  baseAmount: number,
  world: TheWorld
): PaymentChainRow[] {
  const stages = applicableStages(policy, baseAmount);
  const out: PaymentChainRow[] = [];
  stages.forEach((stage, denseRank) => {
    const people = resolveStageMembers(stage.members, world);
    const mode = clampMode(stage.mode, people.length);
    for (const who of people)
      out.push({
        step: stage.id,
        stageSeq: denseRank,
        who,
        ...(mode !== 'all' ? { stageMode: mode } : {}),
        ...(stage.band ? { band: stage.band.minAmountBase } : {}),
      });
  });
  return out;
}

/* ── WHETHER THE CHAIN IS SETTLED — reuse the sign-off state helpers verbatim ──
   PaymentChainRow is shaped as a sign-off row ({step, stageSeq, signedAt, answer}),
   so `stateOf`, `openStep` and `howFar` read it directly. A stage settles under its
   own mode; here we expose the whole-chain verdict and the stage open right now. */

export type PaymentChainState = 'waiting' | 'authorised' | 'rejected';

/** Whole-chain verdict. A stage on 'any one' / a quorum is settled once enough of
 *  its rows are signed; the line is AUTHORISED only when every required stage is. */
export function chainState(rows: PaymentChainRow[]): PaymentChainState {
  if (rows.length === 0) return 'waiting';
  if (rows.some((r) => r.signedAt && r.answer === 'rejected')) return 'rejected';
  // Group by stage; a stage is met when its mode is satisfied by its signed rows.
  const byStage = new Map<string, PaymentChainRow[]>();
  for (const r of rows) {
    const g = byStage.get(r.step);
    if (g) g.push(r);
    else byStage.set(r.step, [r]);
  }
  for (const g of byStage.values()) {
    const signed = g.filter((r) => r.signedAt && r.answer === 'approved').length;
    const mode = g.find((r) => r.stageMode)?.stageMode ?? 'all';
    const need =
      mode === 'anyOne' ? 1 : typeof mode === 'object' ? Math.min(mode.quorum, g.length) : g.length;
    if (signed < need) return 'waiting';
  }
  return 'authorised';
}

/** The stage id open right now, or null when none is (settled, or a refusal ended
 *  it). Delegates to the sign-off order gate. */
export function openPaymentStage(rows: PaymentChainRow[]): string | null {
  return openStep(rows) as string | null;
}

/** How far along, for a line that says so without anybody opening it. */
export { howFar };

/* ── WHO MAY AUTHORISE THE OPEN STAGE RIGHT NOW ────────────────────────────────
   The new rule, and the whole point of the feature: a person may authorise ONLY
   when the chain names them as an unsigned required signer on the stage open now.
   Being a workspace admin grants NOTHING here — the old "any admin / any manager"
   fallback is gone. Separation of duties still holds: whoever accepted the work may
   not authorise its payment, and only a workspace admin may override that, with a
   mandatory reason (a logged control exception), exactly as before. */

export interface PaymentAuthority {
  allowed: boolean;
  /** The row (required signer) this person answers, when allowed. */
  stageId?: string;
  needsReason: boolean;
  /** Allowed only because an admin is overriding separation of duties. */
  sodOverride: boolean;
  /** When not allowed, a plain sentence for the disabled control. */
  blockedWhy?: string;
}

export function paymentAuthority(opts: {
  /** The line's frozen chain. Absent/empty → nothing to sign (chain not raised). */
  chain: PaymentChainRow[];
  /** Who accepted the work (the SoD anchor). */
  acceptedBy?: string;
  /** The person trying to authorise — their email. */
  me: string;
  /** Is `me` a workspace admin — the ONLY tier that may override SoD (never a
   *  fallback to approve on its own). */
  isAdmin: boolean;
}): PaymentAuthority {
  const me = lc(opts.me);
  if (!me) return { allowed: false, needsReason: false, sodOverride: false, blockedWhy: 'Sign in to authorise.' };

  if (opts.chain.some((r) => r.signedAt && r.answer === 'rejected'))
    return { allowed: false, needsReason: false, sodOverride: false, blockedWhy: 'Somebody has declined this payment, so nothing further is asked.' };

  const open = openPaymentStage(opts.chain);
  if (open === null)
    return { allowed: false, needsReason: false, sodOverride: false, blockedWhy: 'This payment is already fully authorised.' };

  // My unsigned row on the stage open right now. Only a named required signer of the
  // open stage may sign — not an admin standing in, not a signer of a later stage.
  const mine = opts.chain.find((r) => r.step === open && lc(r.who.email) === me && !r.signedAt);
  if (!mine)
    return {
      allowed: false,
      needsReason: false,
      sodOverride: false,
      blockedWhy: 'Only a required signer of the stage open now may authorise this payment.',
    };

  // Separation of duties — whoever accepted the work may not authorise its payment.
  const iAccepted = !!lc(opts.acceptedBy) && lc(opts.acceptedBy) === me;
  if (iAccepted) {
    if (opts.isAdmin) return { allowed: true, stageId: open, needsReason: true, sodOverride: true };
    return {
      allowed: false,
      needsReason: false,
      sodOverride: false,
      blockedWhy: 'You verified this work, so you cannot also authorise its payment.',
    };
  }

  return { allowed: true, stageId: open, needsReason: false, sodOverride: false };
}

/* ── WHY A CHAIN CANNOT BE USED — in the words to put on the screen ────────────
   Empty means it can. Checked before the chain is raised AND offered on the setup
   screen, so a board is told its chain is empty while somebody is looking at it.
   `null` policy is the first-run state and is BLOCKED by its own, louder message. */

export function paymentPolicyProblem(
  policy: PaymentApprovalPolicy | null,
  world: TheWorld
): string {
  if (!policy || policy.stages.length === 0)
    return 'No payment approver is set. No payment can be authorised until you add a stage and say who signs it.';
  if (policy.stages.some((s) => !tidy(s.label)))
    return 'Give every stage a name — name the new one, or remove it.';
  const nameOf = (s: PaymentStage) => `The "${tidy(s.label)}"`;
  const emptyMembers = policy.stages.find((s) => s.members.length === 0);
  if (emptyMembers) return `${nameOf(emptyMembers)} stage asks nobody. Add who authorises it, or remove the stage.`;
  if (policy.stages.every((s) => s.optional))
    return 'Every stage here is optional, so nothing has to be authorised. Make at least one stage required.';
  // A stage that resolves to nobody right now (a role matching no one) is a silent
  // break — found the same way the sign-off chain finds it. Check against the
  // all-payments resolution (no band excluded), so an empty stage is caught
  // regardless of amount.
  const allStagesPolicy: PaymentApprovalPolicy = { stages: policy.stages.map((s) => ({ ...s, band: undefined })) };
  const rows = resolvePaymentChain(allStagesPolicy, 0, world);
  const blank = policy.stages.find((s) => rows.every((r) => r.step !== s.id));
  if (blank) return `${nameOf(blank)} stage is set up but asks nobody right now. Check who it authorises, or remove the stage.`;
  return '';
}

/** The whole-chain verdict for a line, given its base amount and the policy — the
 *  one call a screen makes to know where a payment stands. `blocked` when no chain
 *  is set (first-run); otherwise the chain state. */
export function lineChainState(line: Pick<InvoiceLine, 'payChain'>): PaymentChainState | 'blocked' {
  if (!line.payChain || line.payChain.length === 0) return 'blocked';
  return chainState(line.payChain);
}
